/**
 * `diffScope` — elaborate's CONVENTION for the opaque `scope` field of a
 * `@norkron/web-review-workbench` diff permalink token (`DiffLinkToken.scope`).
 *
 * The lib owns the token wire format (`serializeDiffLink` / `parseDiffLink` /
 * `buildDiffPermalink`) but treats `scope` as an opaque string — the host assigns
 * its meaning (see `diffLink.ts` in the lib). elaborate compares either a WHOLE
 * RESOURCE (`resource:<kind>`) or a single SECTION (`section:<sectionId>`), so a
 * `?diff=<token>` must round-trip which of the two it opened AND enough to re-run
 * that exact compare on a cold load. These are the pure, testable encoders/decoders
 * for that convention — no lib code, no fetch, no DOM.
 *
 * RULE ZERO: the generic token machinery lives in the lib; only the elaborate
 * DOMAIN convention (resource-kind vs section id) lives here.
 */

import type { Kind } from "@norkron/web-review-workbench";

const RESOURCE_PREFIX = "resource:";
const SECTION_PREFIX = "section:";

/** A resource-kind we round-trip in a `resource:<kind>` scope. */
const RESOURCE_KINDS: readonly Kind[] = ["brief", "research", "article"];

/** Build the scope for a whole-resource compare, e.g. `resource:article`. */
export function resourceScope(kind: Kind): string {
  return `${RESOURCE_PREFIX}${kind}`;
}

/** Build the scope for a section-scoped compare, e.g. `section:9f2`. */
export function sectionScope(sectionId: string): string {
  return `${SECTION_PREFIX}${sectionId}`;
}

/** The decoded meaning of a `DiffLinkToken.scope` under elaborate's convention. */
export type DiffScope =
  | { readonly kind: "resource"; readonly role: Kind }
  | { readonly kind: "section"; readonly sectionId: string };

/**
 * Decode a `scope` string. TOTAL + safe: returns `null` for anything that does
 * not match the convention (an unknown prefix, an unrecognized resource kind, an
 * empty section id) so a stale / hand-mangled token degrades to "no deep-link"
 * rather than throwing — mirroring `parseDiffLink`'s tolerance.
 */
export function parseDiffScope(scope: string): DiffScope | null {
  if (scope.startsWith(RESOURCE_PREFIX)) {
    const role = scope.slice(RESOURCE_PREFIX.length);
    if ((RESOURCE_KINDS as readonly string[]).includes(role)) {
      return { kind: "resource", role: role as Kind };
    }
    return null;
  }
  if (scope.startsWith(SECTION_PREFIX)) {
    const sectionId = scope.slice(SECTION_PREFIX.length);
    return sectionId === "" ? null : { kind: "section", sectionId };
  }
  return null;
}
